import type { HTMLAttributes, ReactNode } from 'react';

import { PageLayout } from './Layout.js';
import type { PageLayoutProps } from './Layout.js';
import { PageFrame } from './OrnateFrame.js';

/**
 * The master parchment shell: a `flush` `PageLayout` carrying the brass
 * `page` frame right at the plate's edge (APP_SPEC §13, §17; the approved
 * Games-page mockup, which describes the Games and Profile plates as one
 * shell system rather than two pages that happen to be beige).
 *
 * This exists because the shell is **three numbers that have to agree**, and
 * nothing was holding them together:
 *
 *   - the plate's corner radius   (`--px-frame-plate-radius`, read by `.app-frame`)
 *   - the framed panel's radius   (`rounded-[…]`)
 *   - the frame art's corner size (`--px-frame-corner-size`, read by `.px-ornate-frame`)
 *
 * The corner ornament is drawn as a fixed-size square sitting on the panel's
 * corner, so the radius it sweeps through is what the parchment underneath
 * must be cut to. Get them out of step and the plate's square corner shows as
 * a tab of bare parchment outside the brass sweep — which is exactly what
 * `GameHostPage` did, because it hand-wrote two of the three values and
 * omitted the plate radius entirely while `CatalogPage` and `ProfilePage`
 * hand-wrote all three identically. Three pages, one shell, three copies of
 * the same class string, and a one-line divergence nothing could catch.
 *
 * So the geometry lives here once, in `PLATE_SURFACE` and `PLATE_FRAME`
 * below, and callers choose only width, rhythm, and their own content.
 *
 * The two constants stay written out as literal class strings rather than
 * being composed from a token at runtime: Tailwind discovers utilities by
 * scanning source text, so a class assembled from variables is never emitted.
 * Keeping them literal *and* keeping them in one module is what makes this
 * both build-safe and single-sourced.
 */

/**
 * The plate's own corner radius. Cut to follow the sweep of the frame's
 * corner ornament — see `PLATE_FRAME`, whose radius and corner size this must
 * match at both breakpoints.
 */
const PLATE_SURFACE = '[--px-frame-plate-radius:32px] tablet:[--px-frame-plate-radius:40px]';

/**
 * The framed panel: parchment ground, matching radius, and the `page` frame
 * art at the corner size that radius was cut for.
 */
const PLATE_FRAME =
  'bg-parchment shadow-3 rounded-[32px] tablet:rounded-[40px] ' +
  '[--px-frame-corner-size:32px] tablet:[--px-frame-corner-size:40px] ' +
  'p-4 tablet:p-7';

/**
 * Vertical rhythm between the plate's own top-level regions.
 *
 * `regions` is the spacing a page of distinct illustrated sections wants (the
 * profile's hero / Gameplay, the catalog's header / Continue Playing / Start a
 * Game). `compact` is for a plate whose content is one continuous surface
 * rather than a stack of regions — a live game board and its controls.
 */
const GAP_CLASSES = {
  regions: 'gap-8',
  compact: 'gap-4',
} as const;

export interface PagePlateProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /**
   * Passed through to `PageLayout`, whose own default (`wide`, matching
   * `GameHostPage`'s board+rails layout — see its doc comment) this
   * inherits: every page's width should match (2026-09-20).
   */
  width?: PageLayoutProps['width'];
  /** Spacing between the plate's top-level regions. */
  gap?: keyof typeof GAP_CLASSES;
  /** Extra classes for the outer plate element. */
  className?: string;
  /** Extra classes for the framed panel inside it. */
  frameClassName?: string;
  /**
   * Content rendered as a sibling *outside* the framed panel — dialogs and
   * other overlays that belong to the page but must not sit inside its frame.
   */
  overlays?: ReactNode;
}

export function PagePlate({
  children,
  width = 'wide',
  gap = 'regions',
  className = '',
  frameClassName = '',
  overlays,
  ...rest
}: PagePlateProps) {
  return (
    <PageLayout
      width={width}
      surface="flush"
      className={`${PLATE_SURFACE} ${className}`.trim()}
      {...rest}
    >
      <PageFrame
        className={`flex flex-col ${GAP_CLASSES[gap]} ${PLATE_FRAME} ${frameClassName}`.trim()}
      >
        {children}
      </PageFrame>
      {overlays}
    </PageLayout>
  );
}
